import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  getAvailableCourses, 
  getElearningCategories, 
  getInstructors,
  type ElearningCourse, 
  type ElearningCategory,
  type ElearningInstructor
} from '../../services/elearningCourseService';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { MiniCartPreview } from '../../components/elearning/MiniCartPreview';
import { Filter, Search, X, ChevronDown, Loader2, ArrowRight, BookOpen, Users, Clock, Star, Check, CheckCircle, Play, Sparkles, Crown, Diamond, Gem, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud } from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useTenantContext } from '../../context/TenantContext';
import { useElearningCartActions, useElearningCartStats, courseToCartItem } from '../../stores/useElearningCartStore';
import { useToast } from '../../hooks/useToast';
import { useStudentEnrollments } from '../../hooks/useStudentEnrollments';
import { getDomainFilterParams } from '../../hooks/useDomainQuery';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const CourseListPageContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<ElearningCourse[]>([]);
  const [categories, setCategories] = useState<ElearningCategory[]>([]);
  const [instructors, setInstructors] = useState<ElearningInstructor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Utiliser le hook pour vérifier les inscriptions de l'utilisateur
  const { isEnrolledInCourse, isLoading: isLoadingEnrollments, getCourseAction, getEnrollmentInfo } = useStudentEnrollments();
  
  // Filtres avancés
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(window.innerWidth >= 768);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(searchParams.get('categories')?.split(',').filter(Boolean) || []);
  const [selectedInstructors, setSelectedInstructors] = useState<string[]>(searchParams.get('instructors')?.split(',').filter(Boolean) || []);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(searchParams.get('difficulty')?.split(',').filter(Boolean) || []);
  const [priceRange, setPriceRange] = useState<{min: number | null, max: number | null}>({
    min: searchParams.get('priceMin') ? parseInt(searchParams.get('priceMin') || '0') : null,
    max: searchParams.get('priceMax') ? parseInt(searchParams.get('priceMax') || '0') : null
  });
  const [durationRange, setDurationRange] = useState<{min: number | null, max: number | null}>({
    min: searchParams.get('durationMin') ? parseInt(searchParams.get('durationMin') || '0') : null,
    max: searchParams.get('durationMax') ? parseInt(searchParams.get('durationMax') || '0') : null
  });
  
  // Contrôle de l'affichage "voir plus" pour chaque filtre
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllInstructors, setShowAllInstructors] = useState(false);

  const categorySlug = searchParams.get('categorie') || 'all';
  const sortBy = (searchParams.get('tri') as 'created_at' | 'price' | 'title' | 'rating_average' | 'enrollment_count') || 'created_at';
  const sortOrder = searchParams.get('ordre') as 'asc' | 'desc' || 'desc';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 4;

  const { tenant, previewConfig } = useTenantContext();

  // Configuration de l'en-tête E-Learning (preview > tenant)
  const elearningHeaderConfig = React.useMemo(() => {
    const previewHeader = previewConfig?.pages?.elearning?.header;
    const tenantHeader = tenant?.config?.pages?.elearning?.header;
    return {
      badgeLabel: previewHeader?.badgeLabel ?? tenantHeader?.badgeLabel ?? 'Formations d\'excellence',
      title: previewHeader?.title ?? tenantHeader?.title ?? 'Nos formations théologiques',
      line1: previewHeader?.line1 ?? tenantHeader?.line1 ?? 'Approfondissez votre foi avec nos formations théologiques d\'excellence.',
      line2: previewHeader?.line2 ?? tenantHeader?.line2 ?? 'Transformez votre compréhension spirituelle',
    };
  }, [previewConfig?.pages?.elearning?.header, tenant?.config?.pages?.elearning?.header]);

  // Thème E-learning (preview > tenant)
  const elearningTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig, elearningHeaderConfig]);
  const { addItem, isItemInCart } = useElearningCartActions();
  const { toast } = useToast();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { domain, tenantId } = getDomainFilterParams();
      if (!tenantId) return;

      // Charger les catégories
      const categoriesData = await getElearningCategories(tenantId, domain);
      setCategories(categoriesData);

      // Charger les instructeurs
      const instructorsData = await getInstructors(tenantId, domain);
      setInstructors(instructorsData);

      // Charger les cours
      const { courses: coursesData, count } = await getAvailableCourses({
        tenantId: tenantId,
        categorySlug: categorySlug === 'all' ? undefined : categorySlug,
        sortBy,
        sortOrder,
        limit,
        offset: (page - 1) * limit,
        search: searchInput || undefined,
        categoryIds: selectedCategories.length > 0 ? selectedCategories : undefined,
        instructorIds: selectedInstructors.length > 0 ? selectedInstructors : undefined,
        difficultyLevels: selectedDifficulties.length > 0 ? selectedDifficulties : undefined,
        priceMin: priceRange.min !== null ? priceRange.min : undefined,
        priceMax: priceRange.max !== null ? priceRange.max : undefined,
        durationMin: durationRange.min !== null ? durationRange.min : undefined,
        durationMax: durationRange.max !== null ? durationRange.max : undefined
      });
      console.log('Cours chargés:', coursesData);
      console.log('Nombre total de cours:', count);
      setCourses(coursesData);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [categorySlug, sortBy, sortOrder, page, searchParams]);

  // Gestion des filtres
  const handleFilterChange = (param: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(param, value);
    if (param !== 'page') {
      newParams.set('page', '1'); // Reset page seulement si ce n'est pas le paramètre page
    }
    setSearchParams(newParams);
  };

  // Fonction pour appliquer tous les filtres en une fois
  const applyFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    
    // Appliquer tous les filtres locaux
    if (searchInput) {
      newParams.set('search', searchInput);
    } else {
      newParams.delete('search');
    }
    
    if (selectedCategories.length > 0) {
      newParams.set('categories', selectedCategories.join(','));
    } else {
      newParams.delete('categories');
    }
    
    if (selectedInstructors.length > 0) {
      newParams.set('instructors', selectedInstructors.join(','));
    } else {
      newParams.delete('instructors');
    }
    
    if (selectedDifficulties.length > 0) {
      newParams.set('difficulty', selectedDifficulties.join(','));
    } else {
      newParams.delete('difficulty');
    }
    
    if (priceRange.min !== null) {
      newParams.set('priceMin', priceRange.min.toString());
    } else {
      newParams.delete('priceMin');
    }
    
    if (priceRange.max !== null) {
      newParams.set('priceMax', priceRange.max.toString());
    } else {
      newParams.delete('priceMax');
    }
    
    if (durationRange.min !== null) {
      newParams.set('durationMin', durationRange.min.toString());
    } else {
      newParams.delete('durationMin');
    }
    
    if (durationRange.max !== null) {
      newParams.set('durationMax', durationRange.max.toString());
    } else {
      newParams.delete('durationMax');
    }
    
    newParams.set('page', '1'); // Reset page
    setSearchParams(newParams);
  };

  // Fonction pour réinitialiser tous les filtres
  const resetFilters = () => {
    setSearchInput('');
    setSelectedCategories([]);
    setSelectedInstructors([]);
    setSelectedDifficulties([]);
    setPriceRange({ min: null, max: null });
    setDurationRange({ min: null, max: null });
    
    const newParams = new URLSearchParams();
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleAddToCart = (course: ElearningCourse) => {
    if (isItemInCart(course.id)) {
      toast({
        title: 'Déjà dans le panier',
        description: 'Ce cours est déjà dans votre panier',
        variant: 'default'
      });
      return;
    }

    const cartItem = courseToCartItem(course);
    addItem(cartItem);
    
    toast({
      title: 'Cours ajouté',
      description: `${course.title} a été ajouté à votre panier`,
      variant: 'default'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours === 1) return '1 heure';
    return `${hours} heures`;
  };

  const formatDifficulty = (level: string) => {
    const levels = {
      beginner: 'Débutant',
      intermediate: 'Intermédiaire',
      advanced: 'Avancé'
    };
    return levels[level as keyof typeof levels] || level;
  };

  // Configuration SEO personnalisée par tenant (doit être avant les returns conditionnels)
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const elearningHeader = tenant?.config?.pages?.elearning?.header || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${elearningHeader.title || 'Catalogue des Cours'} | ${tenant?.name || 'E-Learning'}`,
      description: seo.description || elearningHeader.line1 || `Découvrez notre catalogue de cours en ligne. Formations professionnelles, développement personnel et compétences techniques.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/elearning/courses`
    };
  }, [tenant]);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {elearningTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: elearningTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[elearningTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                    ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                    ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !elearningTheme.overlay.useGradient
                ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>
          
          <div className="flex flex-col justify-center items-center h-screen relative z-10">
            <div className="relative">
              <div className="w-16 h-16 border-4 rounded-full animate-spin"
                style={{
                  borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                  borderTopColor: tenant?.config?.color1 || '#fe8d17'
                }}
              ></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                style={{ 
                  borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                  animationDirection: 'reverse',
                  animationDuration: '1.5s'
                }}
              ></div>
            </div>
            <span className="mt-6 text-xl font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Chargement des cours...</span>
            <div className="mt-2 flex space-x-1">
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tenant?.config?.color1 || '#fe8d17', animationDelay: '0s' }}></div>
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tenant?.config?.color1 || '#fe8d17', animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tenant?.config?.color1 || '#fe8d17', animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={seoConfig.title}
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />
      
      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {elearningTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: elearningTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[elearningTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                    ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                    ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !elearningTheme.overlay.useGradient
                ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {elearningTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(elearningTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[0].icon] || BookOpen, { size: elearningTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {elearningTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(elearningTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[1].icon] || Sparkles, { size: elearningTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {elearningTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(elearningTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[2].icon] || Diamond, { size: elearningTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {elearningTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(elearningTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[3].icon] || Star, { size: elearningTheme.icons[3].size })}
            </motion.div>
          )}
        </div>
        {/* Contenu de la page */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
            {/* En-tête de la section - Style Contact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
              className="mb-8 py-16 text-center"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl hover:shadow-2xl transition-all duration-300"
                style={{ 
                  borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`,
                  borderWidth: '1px'
                }}
              >
                <BookOpen className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{elearningHeaderConfig.badgeLabel}</span>
                <Sparkles className="w-4 h-4 ml-3 animate-bounce" style={{ color: tenant?.config?.color1 || '#fe8d17', animationDelay: '0.5s' }} />
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 80 }}
                className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg"
                style={{ color: tenant?.config?.color3 || '#ffffff' }}
              >
                {elearningHeaderConfig.title}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-lg drop-shadow-md max-w-3xl mx-auto"
                style={{ color: `${tenant?.config?.color3 || '#ffffff'}e6` }}
              >
                {elearningHeaderConfig.line1}
                <br />
                <span style={{ color: tenant?.config?.color1 || '#fe8d17' }} className="font-semibold">{elearningHeaderConfig.line2}</span>.
              </motion.p>
            </motion.div>

            {/* Contenu principal */}
            <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filtres */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-white/5 backdrop-blur-sm border text-white rounded-2xl p-6 shadow-2xl transition-all duration-500 hover:shadow-3xl"
                  style={{
                    borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`
                  }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                      <Filter className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={20} />
                      Filtres
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className="lg:hidden"
                    >
                      <ChevronDown className={`transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
                    </Button>
                  </div>

                  <div className={`space-y-6 ${showAdvancedFilters ? 'block' : 'hidden lg:block'}`}>
                    {/* Recherche */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Rechercher</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} size={16} />
                        <Input
                          placeholder="Nom du cours..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          className="pl-10 bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    {/* Catégories */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Catégories</label>
                      <div className="space-y-2">
                        {categories.slice(0, showAllCategories ? undefined : 5).map((category) => (
                          <div key={category.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`category-${category.id}`}
                              checked={selectedCategories.includes(category.id)}
                              onCheckedChange={(checked) => {
                                const newCategories = checked 
                                  ? [...selectedCategories, category.id]
                                  : selectedCategories.filter(id => id !== category.id);
                                
                                setSelectedCategories(newCategories);
                              }}
                            />
                            <label
                              htmlFor={`category-${category.id}`}
                              className="text-sm cursor-pointer transition-colors"
                              style={{
                                color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8)
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8);
                              }}
                            >
                              {category.name}
                            </label>
                          </div>
                        ))}
                        {categories.length > 5 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAllCategories(!showAllCategories)}
                            className="text-[#D4AF37] hover:text-[#002366] transition-colors"
                          >
                            {showAllCategories ? 'Voir moins' : `Voir ${categories.length - 5} de plus`}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Niveau de difficulté */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Niveau</label>
                      <div className="space-y-2">
                        {['beginner', 'intermediate', 'advanced'].map((level) => (
                          <div key={level} className="flex items-center space-x-2">
                            <Checkbox
                              id={`difficulty-${level}`}
                              checked={selectedDifficulties.includes(level)}
                              onCheckedChange={(checked) => {
                                const newDifficulties = checked 
                                  ? [...selectedDifficulties, level]
                                  : selectedDifficulties.filter(d => d !== level);
                                
                                setSelectedDifficulties(newDifficulties);
                              }}
                            />
                            <label
                              htmlFor={`difficulty-${level}`}
                              className="text-sm cursor-pointer transition-colors"
                              style={{
                                color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8)
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8);
                              }}
                            >
                              {formatDifficulty(level)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Prix */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Prix (FCFA)</label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          placeholder="Min"
                          value={priceRange.min || ''}
                          onChange={(e) => {
                            const newMin = e.target.value ? parseInt(e.target.value) : null;
                            setPriceRange({...priceRange, min: newMin});
                          }}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                        />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={priceRange.max || ''}
                          onChange={(e) => {
                            const newMax = e.target.value ? parseInt(e.target.value) : null;
                            setPriceRange({...priceRange, max: newMax});
                          }}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-6 border-t" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2) }}>
                      <Button
                        onClick={applyFilters}
                        className="flex-1 shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                          borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                        }}
                      >
                        <Search className="w-4 h-4 mr-2" />
                        Appliquer
                      </Button>
                      <Button
                        onClick={resetFilters}
                        variant="outline"
                        className="transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.8),
                          color: tenant?.config?.color3 || '#fe8d17'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.1);
                          e.currentTarget.style.borderColor = tenant?.config?.color3 || '#fe8d17';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.8);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des cours */}
            <div className="lg:col-span-3">
              {/* Barre de tri */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div className="text-white font-medium text-lg">
                  <span className="font-bold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{totalCount}</span> cours trouvé{totalCount > 1 ? 's' : ''}
                </div>
                <div className="flex items-center space-x-4">
                  <Select value={sortBy} onValueChange={(value) => handleFilterChange('tri', value)}>
                    <SelectTrigger className="w-48 bg-white transition-all duration-300"
                      style={{
                        borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.3),
                        backgroundColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.1),
                        color: tenant?.config?.color3 || '#008aef',
                        '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                      } as React.CSSProperties}
                    >
                      {sortBy === 'created_at' && <SelectValue>Plus récents</SelectValue>}
                      {sortBy === 'price' && <SelectValue>Prix croissant</SelectValue>}
                      {sortBy === 'title' && <SelectValue>Nom A-Z</SelectValue>}
                      {sortBy === 'rating_average' && <SelectValue>Mieux notés</SelectValue>}
                      {sortBy === 'enrollment_count' && <SelectValue>Plus populaires</SelectValue>}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Plus récents</SelectItem>
                      <SelectItem value="price">Prix croissant</SelectItem>
                      <SelectItem value="title">Nom A-Z</SelectItem>
                      <SelectItem value="rating_average">Mieux notés</SelectItem>
                      <SelectItem value="enrollment_count">Plus populaires</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Grille des cours */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6"
              >
                {courses.map((course) => (
                  <motion.div
                    key={course.id}
                    variants={cardVariants}
                    className="group bg-white/5 backdrop-blur-sm border rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
                    style={{
                      borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2),
                      '--tw-border-color': hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                      '--tw-hover-border-color': tenant?.config?.color1 || '#fe8d17'
                    } as React.CSSProperties}
                  >
                    <Link to={`/elearning/courses/${course.slug}`} className="block">
                    <div className="aspect-video relative">
                      {course.thumbnail_url ? (
                        <img
                          src={course.thumbnail_url}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundImage: `linear-gradient(to bottom right, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.1)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1)})` }}>
                          <BookOpen size={48} style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                        </div>
                      )}
                      {course.category && (
                        <div 
                          className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: course.category.color || '#3B82F6' }}
                        >
                          {course.category.name}
                        </div>
                      )}
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs text-white"
                        style={{
                          backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.8)
                        }}
                      >
                        {formatDifficulty(course.difficulty_level)}
                      </div>
                    </div>

                    </Link>
                    <div className="p-6">
                      <Link to={`/elearning/courses/${course.slug}`} className="block">
                        <h3 className="text-lg font-semibold mb-2 line-clamp-2 transition-colors group-hover:text-current"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-hover-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                        >{course.title}</h3>
                      </Link>
                      <p className="text-sm mb-4 line-clamp-2" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>{course.description}</p>

                      {/* Informations du cours - Instructeur, Durée et Reviews sur une ligne pour desktop */}
                      <div className="mb-4">
                        {/* Mobile: Stack vertical */}
                        <div className="block md:hidden space-y-3">
                          {course.instructor && (
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                                <Users size={16} style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                              </div>
                              <span className="text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>{course.instructor.name}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-4 text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>
                            <div className="flex items-center">
                              <Clock size={14} className="mr-1" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.8) }} />
                              {formatDuration(course.duration_hours)}
                            </div>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star}
                                  size={14} 
                                  className={`mr-0.5 ${
                                    star <= Math.round(course.rating_average) 
                                      ? 'text-yellow-400 fill-current' 
                                      : 'text-gray-300'
                                  }`} 
                                />
                              ))}
                              <span className="ml-1 text-xs">({course.rating_average.toFixed(1)})</span>
                            </div>
                          </div>
                        </div>

                        {/* Desktop: Une seule ligne avec les 3 éléments */}
                        <div className="hidden md:flex items-center justify-between text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>
                          {/* Instructeur */}
                          {course.instructor ? (
                            <div className="flex items-center flex-1">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                                <Users size={12} style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                              </div>
                              <span className="text-xs truncate" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>{course.instructor.name}</span>
                            </div>
                          ) : (
                            <div className="flex-1"></div>
                          )}
                          
                          {/* Durée */}
                          <div className="flex items-center flex-1 justify-center">
                            <Clock size={12} className="mr-1" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.8) }} />
                            <span className="text-xs">{formatDuration(course.duration_hours)}</span>
                          </div>
                          
                          {/* Reviews */}
                          <div className="flex items-center flex-1 justify-end">
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star}
                                  size={12} 
                                  className={`mr-0.5 ${
                                    star <= Math.round(course.rating_average) 
                                      ? 'text-yellow-400 fill-current' 
                                      : 'text-gray-300'
                                  }`} 
                                />
                              ))}
                              <span className="ml-1 text-xs">({course.rating_average.toFixed(1)})</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-xl font-bold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                          {formatPrice(course.price)}
                        </div>
                        {isEnrolledInCourse(course.id) ? (
                          (() => {
                            const { label, action, color } = getCourseAction(course.id);
                            return (
                              <Button
                                onClick={() => {
                                  const enrollmentInfo = getEnrollmentInfo(course.id);
                                  if (enrollmentInfo) {
                                    navigate(`/elearning/player/${enrollmentInfo.id}`);
                                  }
                                }}
                                className={`${color} flex items-center gap-1 text-white`}
                                disabled={action === "view"}
                              >
                                {action === "view" ? <CheckCircle size={16} /> : 
                                 action === "continue" ? <ArrowRight size={16} /> : 
                                 <Play size={16} />} {label}
                              </Button>
                            );
                          })()
                        ) : (
                          <Button
                            onClick={() => {
                              if (isItemInCart(course.id)) {
                                navigate('/elearning/cart');
                              } else {
                                handleAddToCart(course);
                              }
                            }}
                            className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                            style={{
                              backgroundColor: isItemInCart(course.id)
                                ? (tenant?.config?.color2 || '#008aef')
                                : (tenant?.config?.color1 || '#fe8d17'),
                              color: tenant?.config?.color3 || '#ffffff',
                              backgroundImage: isItemInCart(course.id)
                                ? `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color2 || '#0070c0'})`
                                : `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                              borderColor: hexToRgba(
                                isItemInCart(course.id)
                                  ? (tenant?.config?.color2 || '#008aef')
                                  : (tenant?.config?.color1 || '#fe8d17'),
                                0.4
                              ),
                              boxShadow: `0 4px 15px ${hexToRgba(isItemInCart(course.id)
                                ? (tenant?.config?.color2 || '#008aef')
                                : (tenant?.config?.color1 || '#fe8d17'), 0.4)}`
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundImage = isItemInCart(course.id)
                                ? `linear-gradient(to right, ${tenant?.config?.color2 || '#0070c0'}, ${tenant?.config?.color2 || '#008aef'})`
                                : `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundImage = isItemInCart(course.id)
                                ? `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color2 || '#0070c0'})`
                                : `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                            }}
                          >
                            {isItemInCart(course.id) ? 'Voir le panier' : 'Ajouter'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pagination */}
              {totalCount > limit && (
                <div className="flex justify-center mt-12">
                  <div className="flex space-x-2">
                    {page > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => handleFilterChange('page', (page - 1).toString())}
                        className="transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                          color: tenant?.config?.color3 || '#ffffff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1);
                          e.currentTarget.style.borderColor = tenant?.config?.color3 || '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2);
                        }}
                      >
                        Précédent
                      </Button>
                    )}
                    {Array.from({ length: Math.ceil(totalCount / limit) }, (_, i) => i + 1)
                      .filter(p => Math.abs(p - page) <= 2)
                      .map(p => (
                        <Button
                          key={p}
                          variant={p === page ? "default" : "outline"}
                          onClick={() => handleFilterChange('page', p.toString())}
                          className={p === page ? "shadow-lg hover:shadow-xl transition-all duration-300 font-bold" : "transition-all duration-200"}
                          style={p === page ? {
                            backgroundColor: tenant?.config?.color1 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff',
                            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                            borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                            boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                          } : {
                            borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                          onMouseEnter={p !== page ? (e) => {
                            e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1);
                            e.currentTarget.style.borderColor = tenant?.config?.color3 || '#ffffff';
                          } : undefined}
                          onMouseLeave={p !== page ? (e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2);
                          } : undefined}
                        >
                          {p}
                        </Button>
                      ))}
                    {page < Math.ceil(totalCount / limit) && (
                      <Button
                        variant="outline"
                        onClick={() => handleFilterChange('page', (page + 1).toString())}
                        className="transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                          color: tenant?.config?.color3 || '#ffffff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1);
                          e.currentTarget.style.borderColor = tenant?.config?.color3 || '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2);
                        }}
                      >
                        Suivant
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
        </div>
      </div>
    </PublicLayout>
  );
};

const CourseListPage: React.FC = () => {
  return (
    <>
      <MiniCartPreview />
      <CourseListPageContent />
    </>
  );
};

export default CourseListPage;
